import { z } from 'zod';

import { AddressSchema, type Address } from './party.js';

/**
 * Customer Portal — shared schemas for the customer-facing self-service surface
 * and the staff-side provisioning of portal logins.
 */

/** Customer portal password — kept simple on purpose: at least 6 characters,
 *  no complexity requirement (a 6-digit numeric password is fine). */
export const PortalPasswordSchema = z
  .string()
  .min(6, 'Password must be at least 6 characters')
  .max(256, 'Password must be at most 256 characters');

// ---- Customer login (portal side) — PHONE + password ----

/**
 * Loose phone input shared by phone login + self-registration. The server
 * normalizes it (digits, last-9 key) — uniqueness/lookup are per-tenant, so
 * these flows are slug-scoped (tenant resolved from the storefront slug).
 */
export const CustomerPhoneSchema = z
  .string()
  .trim()
  .min(6, 'Enter your phone number')
  .max(32, 'Phone is too long');

/** Phone + password login (POST /storefront/:slug/auth/login). */
export const CustomerPhoneLoginSchema = z.object({
  phone: CustomerPhoneSchema,
  password: z.string().min(1),
});
export type CustomerPhoneLogin = z.infer<typeof CustomerPhoneLoginSchema>;

/**
 * Phone-OTP password reset confirm (POST /storefront/:slug/auth/reset/confirm).
 * The code is requested via the existing /auth/otp/request; this sets a new
 * password for the phone's account and signs them in.
 */
export const CustomerPasswordResetSchema = z.object({
  phone: CustomerPhoneSchema,
  code: z.string().trim().min(4).max(8),
  password: PortalPasswordSchema,
});
export type CustomerPasswordReset = z.infer<typeof CustomerPasswordResetSchema>;

/** Customer self-registration (storefront / mobile app). Phone is the identity. */
export const CustomerRegisterSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(120),
  phone: CustomerPhoneSchema,
  password: PortalPasswordSchema,
  /**
   * Optional self-selected customer type — the id of an active price list
   * ("customer group") exposed via StorefrontInfo.customerTypes. Applied only
   * when a NEW party is created (never overrides an existing customer's pricing).
   */
  priceListId: z.string().uuid().optional(),
  /**
   * WhatsApp verification code (registration flow). When provided it is
   * verified + consumed BEFORE the account is created; clients should collect
   * it whenever the store's `otp` module is enabled.
   */
  otpCode: z.string().trim().min(4).max(8).optional(),
});
export type CustomerRegister = z.infer<typeof CustomerRegisterSchema>;

/** The login identity returned to the portal (NOT the full profile — see /portal/me). */
export const PortalCustomerSchema = z.object({
  id: z.string().uuid(),
  /** Phone is the login identity; email is legacy and may be null. */
  phone: z.string().nullable(),
  email: z.string().nullable(),
  partyId: z.string().uuid(),
  status: z.enum(['active', 'disabled']),
  lastLoginAt: z.string().datetime().nullable(),
});
export type PortalCustomer = z.infer<typeof PortalCustomerSchema>;

export const ChangePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: PortalPasswordSchema,
});
export type ChangePasswordInput = z.infer<typeof ChangePasswordSchema>;

// ---- Staff-side provisioning (a customer/party's portal login) ----

/**
 * Enable or reset a party's portal login. The party's PHONE is the login
 * identity (customers sign in with phone + password) — staff only sets the
 * password; the phone is taken from the party record itself.
 */
export const PortalAccessInputSchema = z.object({
  password: PortalPasswordSchema,
});

/** Grant or revoke the manager privilege on an existing portal login. */
export const PortalManagerFlagSchema = z.object({
  isAppManager: z.boolean(),
});
export type PortalManagerFlag = z.infer<typeof PortalManagerFlagSchema>;
export type PortalAccessInput = z.infer<typeof PortalAccessInputSchema>;

/** What staff sees about a party's portal login. */
export const PortalAccessStatusSchema = z.object({
  enabled: z.boolean(),
  /** The phone the customer signs in with (from the party record). */
  phone: z.string().nullable(),
  status: z.enum(['active', 'disabled']).nullable(),
  lastLoginAt: z.string().datetime().nullable(),
  /**
   * Whether this login also sees the read-only MANAGER area in the app — the
   * shop's orders, and every product's cost, stock and history. Meaningful only
   * when `enabled`.
   */
  isAppManager: z.boolean(),
});
export type PortalAccessStatus = z.infer<typeof PortalAccessStatusSchema>;

// ---- Profile (the customer's own party, read + limited self-edit) ----

export interface PortalProfile {
  partyId: string;
  code: string;
  displayName: string;
  email: string | null;
  phone: string | null;
  billingAddress: Address | null;
  shippingAddress: Address | null;
  taxId: string | null;
  paymentTermsDays: number;
  createdAt: string;
  /**
   * True when this login carries the app-manager privilege, so the app can show
   * the manager area. It is only a UI hint — every privileged endpoint re-checks
   * the flag server-side on each request.
   */
  isAppManager?: boolean;
  /**
   * True when the manager can also WRITE products (isAppManager AND the
   * tenant's manager_products module is on) — drives the storefront product
   * page's Edit shortcut. UI hint only; the write routes re-check both.
   */
  canEditProducts?: boolean;
}

/**
 * The only fields a customer may edit on their own profile. The phone number is
 * the login identity (`phone_key`) and is intentionally NOT editable here —
 * changing it would desync the sign-in phone from the contact phone.
 */
export const PortalProfileUpdateSchema = z.object({
  billingAddress: AddressSchema.nullable().optional(),
  shippingAddress: AddressSchema.nullable().optional(),
});
export type PortalProfileUpdate = z.infer<typeof PortalProfileUpdateSchema>;

// ---- Read DTOs (output only; amounts are decimal strings) ----

/** A discount/total amount paired with its currency — never summed across currencies. */
export interface PortalCurrencyAmount {
  currencyCode: string;
  amount: string;
}

export interface PortalInvoiceSummary {
  id: string;
  docNumber: string;
  docDate: string;
  dueDate: string | null;
  // 'approved' appears when the statement is a vendor (AP) statement over bills.
  status: 'issued' | 'approved' | 'paid' | 'cancelled';
  currencyCode: string;
  total: string;
  amountPaid: string;
  balance: string;
  isCreditNote: boolean;
  /** The branch (warehouse) the invoice was sold from — a branch's debt is its invoices. Null = unbranched. */
  warehouseId?: string | null;
}

export interface PortalInvoiceLine {
  lineNo: number;
  description: string | null;
  quantity: string;
  unitPrice: string;
  taxAmount: string;
  lineTotal: string;
}

export interface PortalInvoiceDetail extends PortalInvoiceSummary {
  subtotal: string;
  taxTotal: string;
  notes: string | null;
  lines: PortalInvoiceLine[];
}

export interface PortalPaymentAllocation {
  targetType: string;
  targetId: string;
  targetNumber: string | null;
  amount: string;
}

export interface PortalPaymentSummary {
  id: string;
  docNumber: string;
  paymentDate: string;
  currencyCode: string;
  amount: string;
  method: string | null;
  reference: string | null;
}

export interface PortalPaymentDetail extends PortalPaymentSummary {
  notes: string | null;
  allocations: PortalPaymentAllocation[];
}

export interface PortalListResult<T> {
  items: T[];
  total: number;
}

export interface PortalAgingByCurrency {
  currencyCode: string;
  current: string;
  days_1_30: string;
  days_31_60: string;
  days_61_90: string;
  days_90_plus: string;
  total: string;
}

/**
 * Open-item account statement. The summary + aging reflect the full AR
 * position as of `asOf` (invoiced − paid, using each invoice's amount_paid —
 * the same figure the staff AR aging uses), so the headline balance always
 * ties. `invoices`/`payments` are the activity in the [from, asOf] window.
 */
export interface PortalStatement {
  from: string | null;
  asOf: string;
  summary: Array<{ currencyCode: string; invoiced: string; paid: string; balance: string }>;
  aging: PortalAgingByCurrency[];
  invoices: PortalInvoiceSummary[];
  payments: PortalPaymentSummary[];
}

export interface PortalActivity {
  date: string;
  type: 'invoice' | 'payment';
  reference: string;
  currencyCode: string;
  amount: string;
  status: string | null;
}

export interface PortalDashboard {
  totals: Array<{ currencyCode: string; invoiced: string; paid: string; outstanding: string }>;
  openInvoiceCount: number;
  recent: PortalActivity[];
}

// ---- Notifications (account updates) ----

export interface PortalNotification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  relatedType: string | null;
  relatedId: string | null;
  isRead: boolean;
  createdAt: string;
  readAt: string | null;
}

export interface PortalNotificationsResult {
  items: PortalNotification[];
  total: number;
  unreadCount: number;
}

// ---- Online orders (the customer's own store orders) ----

/** A row in the customer's order history (online sales orders for their party). */
export interface PortalOrderSummary {
  id: string;
  orderNumber: string;
  date: string;
  status: string; // submitted | fulfilled | cancelled | ...
  currencyCode: string;
  total: string;
}

export interface PortalOrdersResult {
  items: PortalOrderSummary[];
  total: number;
}
